// Pure helpers used by the team_assist_proposed_action component. Lives in
// its own module so unit tests can exercise them without pulling in the React
// component tree (and its transitive imports — Button, FontAwesome icons,
// etc., which require a DOM-ish environment).

// choicesPropertyName is the propose-time UI hint the backend injects into the
// LLM tool schema (never a real argument). It is not part of the proposal's
// inputSchema, but we skip it defensively so a hand-crafted schema can't turn
// it into an editable field.
const choicesPropertyName = '_choices';

// extractSchemaProperties returns an ordered node tree describing the editable
// form for the destructive tool's JSON Schema. Each node is one of:
//   - a leaf field   { kind: 'field', name, label, description, type, ... }
//   - an object      { kind: 'object', name, label, description, required, children }
//   - an array       { kind: 'array', name, label, description, required, item }
// `name` is the node's LOCAL property name; the component threads the full
// value path (object keys + array indices). MCP tools are generated from
// GraphQL, so object inputs surface as nested `type: object`/`properties` and
// list inputs as `type: array`/`items`, recursively. Unrecognised types fall
// back to a single-line text input. Returns [] when no schema is present so
// callers can fall back to the raw-args preview.
export function extractSchemaProperties(inputSchema) {
  if (!inputSchema || typeof inputSchema !== 'object') {
    return [];
  }
  const properties = inputSchema.properties;
  if (!properties || typeof properties !== 'object') {
    return [];
  }
  const requiredSet = new Set(Array.isArray(inputSchema.required) ? inputSchema.required : []);
  const nodes = Object.keys(properties)
    .filter(name => name !== choicesPropertyName)
    .map(name => buildNode(name, properties[name], requiredSet.has(name), name));
  return orderProposalNodes(nodes, '');
}

// buildNode converts one JSON Schema property into a node. Objects (with
// properties) and arrays (with items) recurse; everything else — including an
// object without properties (a free-form blob) — becomes a leaf field.
// `path` is the node's dotted schema path from the top of the arguments, matching
// the form `x-dependsOn` names a cascading parent in. Array levels add no segment
// (an item is addressed as `lineItems.lineItemId`, not by row), so an item node
// inherits its array's path.
function buildNode(name, rawSpec, required, path) {
  const spec = rawSpec && typeof rawSpec === 'object' ? rawSpec : {};
  const label = spec.title || name;
  const description = spec.description || '';
  if (isObjectSpec(spec)) {
    return {
      kind: 'object',
      name,
      label,
      description,
      dependsOn: null,
      required,
      children: buildChildren(spec, path),
    };
  }
  if (spec.type === 'array') {
    // The element schema becomes the array's item node. An array with no (or a
    // scalar) items schema yields a scalar-field item.
    return {
      kind: 'array',
      name,
      label,
      description,
      dependsOn: null,
      required,
      item: buildNode(name, spec.items, false, path),
    };
  }
  // Labeled options derived from a `oneOf` of {const, title} entries — the
  // backend folds an id-typed field's enumerated candidates into this shape so
  // the agent picks a human-readable label that resolves to the underlying id.
  // null when the field is not a labeled select.
  const options = oneOfOptions(spec);
  return {
    kind: 'field',
    name,
    label,
    description,
    type: inferFieldType(spec),
    // An id-typed object input the backend gave options for is a leaf: the agent
    // picks the whole entity from one select rather than filling its fields, so
    // its value is an object and an unselected one submits nothing. An object
    // WITHOUT options is the free-form blob the text-input fallback handles (see
    // isObjectSpec), whose value is whatever the agent types.
    valueKind: options && (spec.type === 'object' || spec.properties) ? 'object' : 'scalar',
    enumValues: Array.isArray(spec.enum) ? spec.enum : null,
    options,
    // Dotted path of the field this one depends on (cascading select), named from
    // the top of the arguments — `orderId` for a top-level parent,
    // `input.orderId` or `order.id` for one nested in an object. null when
    // independent. See dependentParentValue.
    dependsOn: typeof spec['x-dependsOn'] === 'string' ? spec['x-dependsOn'] : null,
    required,
  };
}

function buildChildren(spec, path) {
  const props = spec.properties;
  const requiredSet = new Set(Array.isArray(spec.required) ? spec.required : []);
  const children = Object.keys(props)
    .filter(name => name !== choicesPropertyName)
    .map(name => buildNode(name, props[name], requiredSet.has(name), path ? `${path}.${name}` : name));
  return orderProposalNodes(children, path);
}

// isObjectSpec recognises a structured object input — a `type: object` with a
// non-empty `properties` map. A oneOf/enum takes precedence (that is a labeled
// leaf select, not a section), and an object without properties is left to the
// text-field fallback.
function isObjectSpec(spec) {
  if (spec.type !== 'object') {
    return false;
  }
  if (oneOfOptions(spec) || (Array.isArray(spec.enum) && spec.enum.length > 0)) {
    return false;
  }
  return Boolean(spec.properties) && typeof spec.properties === 'object' && Object.keys(spec.properties).length > 0;
}

// nodeKind normalises a node's kind, treating a node without an explicit kind
// as a leaf field so synthetic flat schemas (used widely in tests, and the
// pre-object shape) keep working unchanged.
function nodeKind(node) {
  return node.kind || 'field';
}

// dependencyParentLabel returns the label of the node a dependent field cascades
// from, walking its absolute `dependsOn` path down the node tree (array levels add
// no segment, mirroring the path form — see buildNode). Falls back to the path's
// last segment when the path names no node, so a select's "Select X first" hint
// still reads as a field name.
export function dependencyParentLabel(nodes, dependsOn) {
  const segments = String(dependsOn || '').split('.');
  let siblings = nodes;
  let found = null;
  for (const segment of segments) {
    if (!siblings) {
      // The path ran past a leaf — an id-typed object input renders as a single
      // select, so that select is the control the agent picks the parent in and
      // the `order.id` naming its id resolves to it.
      break;
    }
    const next = siblings.find(node => node.name === segment);
    if (!next) {
      return segments[segments.length - 1];
    }
    found = next;
    const kind = nodeKind(found);
    if (kind === 'object') {
      siblings = found.children;
    } else if (kind === 'array') {
      // An item node carries its array's name, so descend past it into the
      // object's children; a bare-scalar array's item IS the leaf.
      siblings = nodeKind(found.item) === 'object' ? found.item.children : [found.item];
    } else {
      siblings = null;
    }
  }
  return found.label || segments[segments.length - 1];
}

// orderByDependency reorders nodes so a dependent (cascading) node always
// follows the parent it depends on — the agent must pick the order before the
// line item. This includes a container (object/array) that merely *holds* a
// dependent leaf: an array of line items whose id cascades from a top-level
// order is pulled after that order. Declaration order is otherwise preserved
// (stable): each node is emitted in turn, but its parent (and any ancestors)
// are pulled ahead first. A dependency on a missing or cyclic node is ignored
// rather than looping. `levelPath` is the dotted path of the level being ordered,
// which turns a dependent's absolute parent path into the sibling to place first
// (see dependencySiblingName); it is '' at the top level.
export function orderByDependency(fields, levelPath = '') {
  const byName = new Map(fields.map(field => [field.name, field]));
  const ordered = [];
  const placed = new Set();
  const visiting = new Set();
  const place = field => {
    if (placed.has(field.name) || visiting.has(field.name)) {
      return;
    }
    visiting.add(field.name);
    const parentName = dependencySiblingName(field, levelPath);
    if (parentName && byName.has(parentName)) {
      place(byName.get(parentName));
    }
    visiting.delete(field.name);
    placed.add(field.name);
    ordered.push(field);
  };
  fields.forEach(place);
  return ordered;
}

// dependencySiblingName maps a node's absolute cascading-parent path to the
// sibling at `levelPath` that must be ordered before it — the segment of the
// parent path that enters this level. For `input.orderId` at level `input` that
// is `orderId` (the parent leaf itself); for `order.id` at the top level it is
// `order` (the object section holding the parent, since that is what renders as
// a sibling here). Returns null when the parent lives outside this level, so
// there is no sibling to order against.
function dependencySiblingName(node, levelPath) {
  const parentPath = dependencyParentName(node);
  if (!parentPath) {
    return null;
  }
  if (!levelPath) {
    return parentPath.split('.')[0];
  }
  return parentPath.startsWith(`${levelPath}.`) ? parentPath.slice(levelPath.length + 1).split('.')[0] : null;
}

// orderProposalNodes orders a list of sibling input nodes for display in three
// groups:
//   1. cascading (parent/child) inputs — each parent immediately before the
//      inputs that depend on it (the agent must choose the parent first);
//   2. the remaining required inputs;
//   3. the remaining optional inputs.
// Received order is preserved within each group. The schema the backend emits
// is alphabetized (the tool's GraphQL field order is lost upstream), so this
// derives a stable, meaningful order from the inputs' own semantics —
// dependencies and required-ness — rather than their arrival order.
export function orderProposalNodes(nodes, levelPath = '') {
  // orderByDependency guarantees a parent precedes its dependents; the stable
  // partition below then keeps that relative order within the cascade group.
  const ordered = orderByDependency(nodes, levelPath);
  const dependedOn = new Set(nodes.map(node => dependencySiblingName(node, levelPath)).filter(Boolean));
  const isCascading = node => dependencySiblingName(node, levelPath) !== null || dependedOn.has(node.name);
  return [
    ...ordered.filter(isCascading),
    ...ordered.filter(node => !isCascading(node) && node.required),
    ...ordered.filter(node => !isCascading(node) && !node.required),
  ];
}

// dependencyParentName returns the absolute path of the field a node cascades
// from, or null when nothing in the node depends on another field. A leaf
// reports its own dependsOn; a container reports the first dependent descendant
// leaf's parent — so an array/object holding a dependent id is ordered (and
// recognised) as dependent even though the container itself carries no
// dependsOn. The path is absolute wherever it was found, so translating it to a
// sibling at a given level is dependencySiblingName's job.
function dependencyParentName(node) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    for (const child of node.children) {
      const parent = dependencyParentName(child);
      if (parent) {
        return parent;
      }
    }
    return null;
  }
  if (kind === 'array') {
    return node.item ? dependencyParentName(node.item) : null;
  }
  return node.dependsOn || null;
}

// isDependentArray reports whether a node is an array whose item cascades from
// another field, wherever in the arguments that parent sits. Such arrays are
// seeded with (and reset to) a single row so
// the agent always has one dependent item to fill, and start expanded so that
// row is visible.
export function isDependentArray(node) {
  return nodeKind(node) === 'array' && Boolean(dependencyParentName(node));
}

// oneOfOptions extracts {value, label, parentValue?} entries from a JSON Schema
// `oneOf` of {const, title, x-parentValue?} entries. A `const` is an id for a
// scalar field and the whole entity for an id-typed object input, which the
// agent picks as a unit. `parentValue` is carried through for dependent
// (cascading) selects so the option can be filtered by the selected parent —
// always a scalar id, stringified so a numeric one still matches. Returns null
// when the field has no such oneOf so callers can distinguish a labeled select
// from a bare enum / text field.
export function oneOfOptions(spec) {
  if (!Array.isArray(spec.oneOf)) {
    return null;
  }
  const options = spec.oneOf
    .filter(entry => entry && Object.prototype.hasOwnProperty.call(entry, 'const'))
    .map(entry => {
      // An object const has no readable form to fall back on, so an entry the
      // backend left untitled is not an option the agent could choose between.
      const label = entry.title || (isPlainObject(entry.const) ? '' : String(entry.const));
      if (!label) {
        return null;
      }
      const option = { value: entry.const, label };
      const parentValue = entry['x-parentValue'];
      if (typeof parentValue === 'string' || typeof parentValue === 'number') {
        option.parentValue = String(parentValue);
      }
      return option;
    })
    .filter(Boolean);
  return options.length > 0 ? options : null;
}

// optionKey renders an option value as the string identity a <select> can carry
// in its DOM value. Object options stringify: two different entities never
// collapse the way String(object) would.
export function optionKey(value) {
  return isPlainObject(value) || Array.isArray(value) ? JSON.stringify(value) : String(value);
}

// sameOptionValue reports whether a form value is the one an option carries.
// Objects compare by structure, so a value seeded from the LLM's arguments
// matches its option regardless of key order; scalars keep the string-loose
// comparison a numeric const against a text-input value needs.
export function sameOptionValue(a, b) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (typeof a === 'object' || typeof b === 'object') {
    if (typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) {
      return false;
    }
    if (Array.isArray(a)) {
      return a.length === b.length && a.every((item, index) => sameOptionValue(item, b[index]));
    }
    const keys = Object.keys(a);
    return (
      keys.length === Object.keys(b).length &&
      keys.every(key => Object.prototype.hasOwnProperty.call(b, key) && sameOptionValue(a[key], b[key]))
    );
  }
  return String(a) === String(b);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

// visibleOptionsFor returns the options a (possibly dependent) select should
// show given the current form values. For a field with `dependsOn`, only
// options whose `parentValue` matches the selected parent are shown, and none
// are shown until the parent is selected — so the agent can't pick a child that
// belongs to a different parent. Independent fields return all their options.
// `formValues` is the top-level values and `path` the field's own value path,
// since a dependent field names its parent by absolute path (see
// dependentParentValue).
export function visibleOptionsFor(field, formValues, path) {
  if (!field.dependsOn) {
    // Pass labeled options through unchanged, and `null` through as `null` so a
    // bare-enum field (no labeled options) still falls back to its enumValues
    // in the renderer rather than rendering an empty select.
    return field.options;
  }
  const options = field.options || [];
  const parentValue = dependentParentValue(field, formValues, path);
  if (parentValue == null || parentValue === '') {
    return [];
  }
  return options.filter(option => option.parentValue === String(parentValue));
}

// dependentParentValue resolves the current value of a dependent field's
// cascading parent. `dependsOn` is the parent's absolute dotted path — the same
// form the backend keys propose-time candidates by — so this walks it down from
// the top-level values regardless of where the child sits: `orderId` for a
// top-level parent, `input.orderId` for one in the same wrapper the child lives
// in, `order.id` for one in a sibling object the child is not inside at all.
// A dotted path carries no row index, so when a segment lands on an array the
// index comes from `path`, the field's own value path — that resolves a parent
// sitting in the same row as the field. Returns undefined when any segment is
// missing, or when an array is crossed that `path` gives no index for, which
// reads as "parent not chosen yet" and leaves the child select empty.
export function dependentParentValue(field, formValues, path) {
  return resolveDependentParent(field, formValues, path).value;
}

// resolveDependentParent resolves a dependent field's parent and reports whether
// reaching it consumed a row index from the field's own path — i.e. the parent
// sits in the same array row as the field, so it scopes that row alone and not
// the whole array.
function resolveDependentParent(field, formValues, path) {
  const ownPath = Array.isArray(path) ? path : [];
  let node = formValues;
  let depth = 0;
  let usedOwnRow = false;
  for (const segment of String(field.dependsOn || '').split('.')) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      return { value: undefined, usedOwnRow };
    }
    if (!Object.prototype.hasOwnProperty.call(node, segment)) {
      return { value: undefined, usedOwnRow };
    }
    node = node[segment];
    if (ownPath[depth] === segment) {
      depth += 1;
    }
    if (Array.isArray(node)) {
      const index = ownPath[depth];
      if (typeof index !== 'number') {
        return { value: undefined, usedOwnRow };
      }
      node = node[index];
      depth += 1;
      usedOwnRow = true;
    }
  }
  return { value: node, usedOwnRow };
}

// applyFieldChange returns the next form values with the leaf at `path` (object
// keys and array indices) set to `value`, then resets every dependent that no
// longer belongs to its (now-current) parent. A cascading parent can itself sit
// at any depth — e.g. an orderId inside a wrapping `input` object — so this
// applies to nested changes too, not just top-level ones. `nodes` is the full
// node tree. See resetStaleDependents.
export function applyFieldChange(formValues, nodes, path, value) {
  return resetStaleDependents(setAtPath(formValues, path, value), nodes);
}

// resetStaleDependents walks the full node tree and resets every dependent
// (cascading) input whose current value no longer belongs to its parent, given
// the other form values:
//   - a dependent scalar leaf — at ANY depth, including inside an object
//     section — is cleared to empty (its stale value is not a visible option
//     for the selected parent, so the select already shows nothing);
//   - a dependent array is reset to a single blank row, since every existing
//     row's child id belonged to the old parent and the agent still needs one
//     row to fill for the new parent.
// Each dependent leaf resolves its parent by absolute path from the top-level
// values (see dependentParentValue), so a parent at any depth — top level, or
// inside an object section, whether or not the child sits in it — is honored. It
// repeats until stable so a chain (A -> B -> C) fully settles — clearing B can
// in turn invalidate C. Shared by applyFieldChange (edit time) and
// initialFormValuesFor (seed time) so a child that doesn't belong to its parent
// is never executable, whether the agent changed the parent or the model
// pre-filled a mismatched pair. Returns the same reference when nothing is
// stale so a no-op change doesn't churn React state.
export function resetStaleDependents(formValues, nodes) {
  let result = formValues;
  let changed = true;
  while (changed) {
    changed = false;
    nodes.forEach(node => {
      const before = result[node.name];
      const after = resetNodeValue(node, before, result, [node.name]);
      if (after !== before) {
        result = { ...result, [node.name]: after };
        changed = true;
      }
    });
  }
  return result;
}

// resetNodeValue returns the value for one node with any stale dependent inside
// it reset, or the same reference when nothing is stale. `root` is the top-level
// form values and `path` the node's own value path, which together resolve a
// dependent leaf's parent — the same resolution the rendered select uses (see
// dependentParentValue).
function resetNodeValue(node, value, root, path) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    if (!value || typeof value !== 'object') {
      return value;
    }
    let next = value;
    node.children.forEach(child => {
      const before = next[child.name];
      const after = resetNodeValue(child, before, root, [...path, child.name]);
      if (after !== before) {
        next = { ...next, [child.name]: after };
      }
    });
    return next;
  }
  if (kind === 'array') {
    const items = Array.isArray(value) ? value : [];
    // A parent outside the array scopes every row, so one stale row means the
    // whole array belonged to the previous parent. A parent inside a row scopes
    // that row alone and is cleared by the per-row pass below instead.
    if (items.some((item, index) => hasStaleDependent(node.item, item, root, [...path, index]))) {
      return [defaultValueForNode(node.item)];
    }
    let next = value;
    items.forEach((item, index) => {
      const after = resetNodeValue(node.item, item, root, [...path, index]);
      if (after !== item) {
        next = next === value ? [...items] : next;
        next[index] = after;
      }
    });
    return next;
  }
  if (node.dependsOn && leafEngaging(value) && !isVisibleOption(node, value, root, path)) {
    return defaultValueForNode(node);
  }
  return value;
}

// hasStaleDependent reports whether a node holds a dependent leaf whose engaged
// value is no longer visible for its parent, counting only parents outside the
// leaf's own row — the test that decides whether a dependent array must be reset
// wholesale rather than row by row.
function hasStaleDependent(node, value, root, path) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    return node.children.some(child =>
      hasStaleDependent(child, value == null ? undefined : value[child.name], root, [...path, child.name])
    );
  }
  if (kind === 'array') {
    const items = Array.isArray(value) ? value : [];
    return items.some((item, index) => hasStaleDependent(node.item, item, root, [...path, index]));
  }
  if (!node.dependsOn || !leafEngaging(value)) {
    return false;
  }
  if (resolveDependentParent(node, root, path).usedOwnRow) {
    return false;
  }
  return !isVisibleOption(node, value, root, path);
}

// isVisibleOption reports whether a dependent leaf's value is among the options
// visible for the currently selected parent, resolved from the top-level values
// and the leaf's own path.
function isVisibleOption(node, value, root, path) {
  return visibleOptionsFor(node, root, path).some(option => sameOptionValue(option.value, value));
}

export function inferFieldType(spec) {
  if (oneOfOptions(spec) || (Array.isArray(spec.enum) && spec.enum.length > 0)) {
    return 'enum';
  }
  switch (spec.type) {
    case 'integer':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'string':
      // The backend only emits the `date-time` JSON Schema format, never
      // `date`, so any other format (including `date`) falls back to text.
      if (spec.format === 'date-time') {
        return 'date';
      }
      return 'text';
    default:
      return 'text';
  }
}

// defaultValueForNode builds the empty value for a node — used to seed a
// missing branch and to append a blank array item. Object → an object of its
// children's defaults, array → [], boolean field → false, other fields → "".
export function defaultValueForNode(node) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    const value = {};
    node.children.forEach(child => {
      value[child.name] = defaultValueForNode(child);
    });
    return value;
  }
  if (kind === 'array') {
    return [];
  }
  return node.type === 'boolean' ? false : '';
}

// initialFormValuesFor prefills the form from the LLM's proposed Arguments
// blob (object or JSON-string), producing a nested value that mirrors the tool
// arguments (scalar → primitive, object → {…}, array → […]). Only keys declared
// in the schema are kept — the schema is the contract. Arrays are seeded with
// one built item per element in the args array; a dependent array the model left
// empty is still seeded with one blank row so the agent has one item to fill.
//
// One rule governs the leaves: nothing survives seeding that the agent cannot
// see in the control it would execute from. Each is seeded in the form its
// control displays and emits — a date in the bare datetime-local shape (see
// iso8601ToDatetimeLocal), a checkbox as a real boolean, a number input as a
// finite number, a text input as a string — and a proposed value with no such
// form collapses to "". A select keeps its value only when the schema lists it
// as an option, and a dependent (cascading) one only when it also belongs to its
// seeded parent, at any depth (the same check that runs on edit; see
// resetStaleDependents).
export function initialFormValuesFor(args, nodes) {
  const parsed = parseProposedActionArguments(args);
  const initial = {};
  nodes.forEach(node => {
    initial[node.name] = seedValueForNode(node, ownArgument(parsed, node.name));
  });
  return resetStaleDependents(initial, nodes);
}

function seedValueForNode(node, argValue) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    const source = argValue && typeof argValue === 'object' && !Array.isArray(argValue) ? argValue : {};
    const value = {};
    node.children.forEach(child => {
      value[child.name] = seedValueForNode(child, ownArgument(source, child.name));
    });
    return value;
  }
  if (kind === 'array') {
    const source = Array.isArray(argValue) ? argValue : [];
    const items = source.map(itemValue => seedValueForNode(node.item, itemValue));
    // A dependent array always shows one row to choose from, even when the
    // model prefilled none — the agent adds more with the Add button if needed.
    if (items.length === 0 && isDependentArray(node)) {
      return [defaultValueForNode(node.item)];
    }
    return items;
  }
  if (argValue === undefined) {
    return node.type === 'boolean' ? false : '';
  }
  if (node.type === 'enum') {
    // The agent picks from a select, so the seed has to be one of the options
    // that select renders — a value the LLM pre-filled but the schema does not
    // list would execute while the control shows nothing, and the agent has no
    // way to see it, let alone change it. Same option set the field renders
    // (labeled options, else bare enum values), and the option's own value is
    // kept so a numeric const stays numeric and an id-typed object input keeps
    // the whole entity.
    const options = node.options || (node.enumValues || []).map(value => ({ value }));
    const match = options.find(option => sameOptionValue(option.value, argValue));
    return match ? match.value : '';
  }
  if (node.type === 'date') {
    return iso8601ToDatetimeLocal(argValue);
  }
  if (node.type === 'boolean') {
    // Boolean("false") is true, so a checkbox seeded with anything but a real
    // boolean would render checked while executing as whatever the model wrote.
    return argValue === true;
  }
  if (node.type === 'number') {
    // The control emits a number or "", and renders neither NaN nor a structure
    // — a number input given one shows an empty field, which reads as untouched.
    const numeric = typeof argValue === 'string' && argValue.trim() !== '' ? Number(argValue) : argValue;
    return typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : '';
  }
  // A text input shows and emits a string, and String() renders an object as
  // "[object Object]" and an array as its comma-joined items — a placeholder for
  // the structure that would execute, not the structure itself. A primitive is
  // kept in the form the control displays it in.
  return typeof argValue === 'object' ? '' : String(argValue);
}

// The schema names the keys, and a tool is free to declare one that Object's
// prototype also carries (toString, constructor, …) — an inherited read would
// hand seeding a native function to stringify into the field.
function ownArgument(source, name) {
  return Object.prototype.hasOwnProperty.call(source, name) ? source[name] : undefined;
}

export function parseProposedActionArguments(args) {
  if (args == null) {
    return {};
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(args);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  if (typeof args === 'object') {
    return args;
  }
  return {};
}

// getAtPath reads the value at a path (object keys and array indices) out of a
// nested form value, tolerating missing intermediate branches.
export function getAtPath(value, path) {
  return path.reduce((acc, segment) => (acc == null ? undefined : acc[segment]), value);
}

// setAtPath returns a copy of `root` with the value at `path` replaced,
// cloning each object/array along the way so React sees new references. A
// numeric segment addresses an array index, a string segment an object key.
export function setAtPath(root, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  if (typeof head === 'number') {
    const arr = Array.isArray(root) ? root.slice() : [];
    arr[head] = rest.length ? setAtPath(arr[head], rest, newValue) : newValue;
    return arr;
  }
  const obj = root && typeof root === 'object' && !Array.isArray(root) ? { ...root } : {};
  obj[head] = rest.length ? setAtPath(obj[head], rest, newValue) : newValue;
  return obj;
}

// appendArrayItem returns a copy of `root` with `itemValue` pushed onto the
// array at `path`.
export function appendArrayItem(root, path, itemValue) {
  const current = getAtPath(root, path);
  const next = Array.isArray(current) ? current.slice() : [];
  next.push(itemValue);
  return setAtPath(root, path, next);
}

// removeArrayItem returns a copy of `root` with the item at `index` removed
// from the array at `path`.
export function removeArrayItem(root, path, index) {
  const current = getAtPath(root, path);
  const next = Array.isArray(current) ? current.slice() : [];
  next.splice(index, 1);
  return setAtPath(root, path, next);
}

// Row keys give every array row a React key that stays with that row's data.
// Positional keys would not: removing a row shifts the rows below it up an
// index, so React keeps the component instance that sat at that index and the
// surviving row inherits the removed row's local UI state — an optional nested
// section renders collapsed while still holding (and submitting) the data the
// agent filled in. Only the values are controlled form state; a section's
// expanded flag lives in the section itself, which is why row identity has to
// be real rather than positional.
//
// The keys mirror the shape of the values: an array's mirror is
// { keys: [one per row], rows: [one child mirror per row] } and an object's is
// { [childName]: mirror } for each child that contains an array. A removed row
// therefore takes its nested arrays' keys with it, leaving the rows below with
// the keys they already had.
export function buildRowKeys(nodes, formValues) {
  const mirror = {};
  nodes.forEach(node => {
    const child = buildNodeRowKeys(node, formValues == null ? undefined : formValues[node.name]);
    if (child) {
      mirror[node.name] = child;
    }
  });
  return mirror;
}

// appendRowKeys returns the mirror with a fresh key appended for a new row of
// the array at `path`.
export function appendRowKeys(rowKeys, path) {
  return updateRowKeysAtPath(rowKeys, path, array => ({
    keys: [...array.keys, nextRowKey()],
    rows: [...array.rows, null],
  }));
}

// removeRowKeys returns the mirror with the key (and nested mirror) of the row
// at `index` of the array at `path` dropped.
export function removeRowKeys(rowKeys, path, index) {
  return updateRowKeysAtPath(rowKeys, path, array => ({
    keys: array.keys.filter((_, i) => i !== index),
    rows: array.rows.filter((_, i) => i !== index),
  }));
}

// rowKeysFor returns one key per rendered row of an array. Padding a mirror
// that has fallen behind its values keeps the keys unique, which is what stops
// two rows from ever sharing a component instance.
export function rowKeysFor(mirror, count) {
  const keys = mirror && Array.isArray(mirror.keys) ? mirror.keys : [];
  return Array.from({ length: count }, (unused, index) => keys[index] || `unkeyed-${index}`);
}

// nestedRowKeys returns the mirror belonging to one row of an array, which is
// what its nested arrays key their own rows from.
export function nestedRowKeys(mirror, index) {
  return mirror && Array.isArray(mirror.rows) ? mirror.rows[index] : null;
}

function buildNodeRowKeys(node, value) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    const mirror = {};
    node.children.forEach(child => {
      const sub = buildNodeRowKeys(child, value == null ? undefined : value[child.name]);
      if (sub) {
        mirror[child.name] = sub;
      }
    });
    return Object.keys(mirror).length > 0 ? mirror : null;
  }
  if (kind === 'array') {
    const items = Array.isArray(value) ? value : [];
    return { keys: items.map(() => nextRowKey()), rows: items.map(item => buildNodeRowKeys(node.item, item)) };
  }
  return null;
}

// updateRowKeysAtPath applies `update` to the array mirror at a value path,
// creating the branches it walks through, so an array that has never been keyed
// (one inside a row the agent just added) is keyed on first use.
function updateRowKeysAtPath(mirror, path, update) {
  if (path.length === 0) {
    return update(asArrayMirror(mirror));
  }
  const [head, ...rest] = path;
  if (typeof head === 'number') {
    const array = asArrayMirror(mirror);
    const rows = array.rows.slice();
    rows[head] = updateRowKeysAtPath(rows[head], rest, update);
    return { ...array, rows };
  }
  const object = mirror && typeof mirror === 'object' ? mirror : {};
  return { ...object, [head]: updateRowKeysAtPath(object[head], rest, update) };
}

function asArrayMirror(mirror) {
  return {
    keys: mirror && Array.isArray(mirror.keys) ? mirror.keys : [],
    rows: mirror && Array.isArray(mirror.rows) ? mirror.rows : [],
  };
}

// Keys only have to be unique among the rows of one array, so a single counter
// for the module is enough.
let rowKeySequence = 0;

function nextRowKey() {
  rowKeySequence += 1;
  return `row-${rowKeySequence}`;
}

// isEngaged reports whether a branch holds any real data — i.e. any descendant
// scalar leaf has an engaging value. Optional object/array sections that are
// not engaged are omitted from the executed arguments and do not enforce their
// inner required fields. An unchecked boolean and an empty string do not engage.
export function isEngaged(node, value) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    return node.children.some(child => isEngaged(child, value == null ? undefined : value[child.name]));
  }
  if (kind === 'array') {
    return Array.isArray(value) && value.some(item => isEngaged(node.item, item));
  }
  return leafEngaging(value);
}

// leafEngaging: whether a scalar leaf value represents real agent-entered data.
function leafEngaging(value) {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'boolean') {
    return value;
  }
  return true;
}

// leafFilled: whether a required scalar leaf is satisfied. Matches the legacy
// semantics — null/undefined and empty/whitespace strings are missing, but 0
// and false count as present.
function leafFilled(value) {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return true;
}

// datetimeLocalToIso8601 converts an <input type="datetime-local"> value
// (local wall-clock with no seconds or zone, e.g. "2026-06-11T15:01") into a
// full ISO 8601 instant in UTC ("2026-06-11T22:01:00Z"). The backend tool
// schema's `date-time` format requires seconds and a timezone, neither of
// which the native control emits; the zone is the agent's browser-local zone,
// matching how the rest of the app derives timezone (Intl / moment.tz.guess).
// Fractional seconds are deliberately dropped — the backend's RFC 3339
// validator expects the second-precision form (e.g. "2026-06-12T14:00:00Z"),
// not the millisecond form Date.toISOString() emits by default.
// Returns the input unchanged when it is empty or not parseable so we never
// silently drop what the agent entered.
export function datetimeLocalToIso8601(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// iso8601ToDatetimeLocal converts a full ISO 8601 instant (the wire format the
// LLM prefills date-time arguments in) into the local-time "YYYY-MM-DDThh:mm"
// string the native datetime-local control requires to display a value.
// Returns "" for anything the control can't render — empty, whitespace,
// non-string, or an unparseable date — mirroring what the control actually
// shows for such a value (nothing). This is also the canonical "what the field
// displays" form used to seed form state, so validation and submission operate
// on the same value the agent sees; re-rendering a value already in the bare
// datetime-local shape is a no-op round-trip.
export function iso8601ToDatetimeLocal(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const pad = n => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

// normalizeFormValuesForSubmit converts the nested editable form value into the
// wire representation the backend expects before it becomes the executed
// arguments. Date-time leaves are promoted to full ISO 8601 instants. Un-engaged
// optional object/array branches are pruned entirely (so an untouched optional
// section is never sent), and entirely-empty items are dropped from arrays;
// children are processed before parents so an emptied child prunes before its
// parent is judged. An empty leaf is dropped at every depth — an untouched field
// is absent from the arguments rather than present as "" — except a checkbox,
// which always sends its true/false.
export function normalizeFormValuesForSubmit(formValues, nodes) {
  const result = {};
  nodes.forEach(node => {
    const value = formValues == null ? undefined : formValues[node.name];
    const normalized = normalizeNode(node, value);
    if (normalized.keep) {
      result[node.name] = normalized.value;
    }
  });
  return result;
}

function normalizeNode(node, value) {
  const kind = nodeKind(node);
  if (kind === 'object') {
    const obj = {};
    node.children.forEach(child => {
      const childValue = value == null ? undefined : value[child.name];
      const normalized = normalizeNode(child, childValue);
      if (normalized.keep) {
        obj[child.name] = normalized.value;
      }
    });
    return { value: obj, keep: node.required || isEngaged(node, value) };
  }
  if (kind === 'array') {
    const items = Array.isArray(value) ? value : [];
    const out = [];
    items.forEach(itemValue => {
      // Drop entirely-empty items so a blank row the agent added but never
      // filled is not sent as an empty object/string.
      if (isEngaged(node.item, itemValue)) {
        out.push(normalizeNode(node.item, itemValue).value);
      }
    });
    return { value: out, keep: node.required || out.length > 0 };
  }
  if (node.valueKind === 'object') {
    // "" is how an unselected object select reads; sending it would put a string
    // where the tool declares an object.
    return { value, keep: isPlainObject(value) };
  }
  const normalized = node.type === 'date' ? datetimeLocalToIso8601(value) : value;
  // An empty control means "not set", so the leaf is dropped rather than sent as
  // "": "" is not a member of an enum and not an RFC 3339 instant, so the tool
  // rejects the whole call over a field the agent deliberately left alone, and on
  // a plain string it would ask the tool to write an empty value where nothing
  // was entered. A checkbox is the exception — unchecked is a value the agent can
  // see in the form, and omitting it would hand a destructive call's behavior to
  // whatever the tool defaults to.
  return { value: normalized, keep: node.type === 'boolean' || leafEngaging(value) };
}

// findMissingRequired returns the paths of required leaves that are still empty
// and "active". A required leaf is active when every optional ancestor branch
// (object, or array item) is engaged — required ancestors are always active. So
// an untouched optional section does not block Execute, but once the agent
// starts filling it, its required fields must be completed. A required array with
// no engaged item is itself reported missing, since it would submit as [].
export function findMissingRequired(formValues, nodes) {
  const missing = [];
  nodes.forEach(node => {
    const value = formValues == null ? undefined : formValues[node.name];
    walkMissing(node, value, true, [node.name], missing);
  });
  return missing;
}

function walkMissing(node, value, ancestorsActive, path, missing) {
  const kind = nodeKind(node);
  if (kind === 'field') {
    if (node.required && ancestorsActive && !leafFilled(value)) {
      missing.push(pathKey(path));
    }
    return;
  }
  const engaged = isEngaged(node, value);
  // Only an array answers for its own emptiness: it submits as [] and its blank
  // rows suppress their required leaves, so nothing else would report it. A
  // required object always submits and every required leaf under it reports
  // itself, so judging the object by engagement only ever blocks Execute over
  // content that is complete — an unchecked required checkbox (false is filled
  // but not engaging) or a section whose fields are all optional. These paths
  // reach no control, so such a block would show the agent nothing to fix.
  if (kind === 'array' && node.required && ancestorsActive && !engaged) {
    missing.push(pathKey(path));
  }
  const branchActive = ancestorsActive && (node.required || engaged);
  if (kind === 'object') {
    node.children.forEach(child => {
      const childValue = value == null ? undefined : value[child.name];
      walkMissing(child, childValue, branchActive, [...path, child.name], missing);
    });
    return;
  }
  // array
  const items = Array.isArray(value) ? value : [];
  items.forEach((itemValue, index) => {
    // Only enforce an item's required leaves for items the agent actually
    // began filling — a blank row is dropped at submit, so it must not block.
    const itemActive = branchActive && isEngaged(node.item, itemValue);
    walkMissing(node.item, itemValue, itemActive, [...path, index], missing);
  });
}

// pathKey renders a value path as a stable string key. Top-level leaves stay
// their bare name (matching the pre-object shape); nested keys join with "."
// and array indices appear as their own segment (e.g. "lineItems.0.sku").
function pathKey(path) {
  return path.join('.');
}

// fieldControlId derives the DOM id of a control from its path-derived field
// key. Both the field's own label and an array item caption standing in for
// that label resolve the id here so the two cannot drift apart.
export function fieldControlId(fieldKey) {
  return `team-assist-pa-${fieldKey}`;
}

// formatProposedActionArguments renders the LLM-emitted argument blob as
// pretty-printed JSON text. Always returns text — never HTML — so the
// caller can safely place it inside a <pre> without dangerouslySetInnerHTML.
// Accepts either a plain object or a JSON string (the wire format from
// supernova).
export function formatProposedActionArguments(args) {
  if (args == null) {
    return '';
  }
  if (typeof args === 'string') {
    if (!args.trim()) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(args), null, 2);
    } catch (e) {
      return args;
    }
  }
  try {
    return JSON.stringify(args, null, 2);
  } catch (e) {
    return String(args);
  }
}
